import React, { useEffect, useRef, useState } from 'react'
import DateTimePicker from '../DateTimePicker'

// Multi-select dropdown component
const MultiSelectDropdown = ({ field, formData, handleChange, isReadOnly = false }) => {
  const multiSelectValues = (((formData && formData[field.key] !== undefined) ? formData[field.key] : field.value) || '').split(',').filter(v => v.trim())
  const allSelected = multiSelectValues.length === field.options.length && field.options.length > 0
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])
  
  // Get display text for the dropdown trigger
  const getDisplayText = () => {
    if (multiSelectValues.length === 0) {
      return '選択してください'
    } else {
      // Show the actual selected option names
      const selectedOptions = multiSelectValues
        .map(value => field.options.find(opt => opt.key === value))
        .filter(opt => opt) // Remove any undefined values
        .map(opt => opt.satsueiMemoColumnValueName)
      
      return selectedOptions.join(', ')
    }
  }
  
  if (isReadOnly) {
    return (
      <div className="flex gap-2 flex-wrap">
        {field.options.map((option, index) => (
          <div
            key={index}
            className={`px-3 py-2 border rounded-md text-sm ${
              multiSelectValues.includes(option.key)
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-gray-100 text-gray-500 border-gray-300'
            }`}
          >
            {option.satsueiMemoColumnValueName}
          </div>
        ))}
      </div>
    )
  }
  
  return (
    <div className="relative" ref={dropdownRef} data-testid={`multiselect-dropdown-${field.key}`}>
      {/* Dropdown Trigger */}
      <button
        type="button"
        data-testid={`multiselect-trigger-${field.key}`}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left bg-white relative cursor-default hover:border-gray-300 hover:bg-white"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${field.label}を選択`}
      >
        <span className="text-sm text-gray-700 truncate pr-6">
          {getDisplayText()}
        </span>
        {/* CSS-only dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
          <div className="w-2 h-2 border-b-2 border-r-2 border-black rotate-45"></div>
        </div>
      </button>
      
      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute z-10 w-full border border-black bg-white shadow-lg overflow-hidden">
          {/* Select All Button */}
          <div className="border-b border-gray-200">
            <button
              type="button"
              onClick={() => {
                if (allSelected) {
                  // Deselect all
                  handleChange('')
                } else {
                  // Select all
                  const allValues = field.options.map(option => option.key)
                  handleChange(allValues.join(','))
                }
              }}
              className="w-full px-3 py-2 text-left text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              {allSelected ? 'すべて解除' : 'すべて選択'}
            </button>
          </div>
          
          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {field.options.map((option, index) => {
              const isSelected = multiSelectValues.includes(option.key)
              return (
                <label
                  key={index}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-0"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...multiSelectValues, option.key]
                        : multiSelectValues.filter(v => v !== option.key)
                      handleChange(newValues.join(','))
                    }}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">
                    {option.satsueiMemoColumnValueName}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const SatsueiDataFormArea = ({
  // Mode configuration
  mode = 'edit', // 'edit' | 'share' | 'view'
  
  // Data props
  formData,
  updateFormData,
  satsueiMemoCategories,
  satsueiMemoCategoryId,
  onCategoryChange,
  kyotenList,
  kyotenId,
  setKyotenId,
  createUserName,
  createDatetime,
  updateUserName,
  updateDatetime,
  hasData,
  satsueiMemoColumnDataList,
  
  // Share-specific props
  satsueiGuideList,
  satsueiGuideId,
  setSatsueiGuideId
}) => {
  const formRef = useRef(null)
  const [parsedFormData, setParsedFormData] = useState([])
  
  // Determine if form is read-only based on mode
  const isReadOnly = mode === 'share' || mode === 'view'
  
  // Helper function to convert backend date format to input format
  const convertBackendDateToInputFormat = (backendDate, isDateTime = false) => {
    if (!backendDate) return ''

    try {
      if (isDateTime) {
        // Convert "2025/09/02 11:00" to "2025-09-02 11:00" (DateTimePicker format)
        // Simply replace slashes with dashes
        return backendDate.replace(/\//g, '-')
      } else {
        // Convert "2025/09/03" to "2025-09-03"
        const date = new Date(backendDate.replace(/\//g, '-'))
        if (isNaN(date.getTime())) return ''

        // Format as YYYY-MM-DD
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')

        return `${year}-${month}-${day}`
      }
    } catch (error) {
      console.error('Error converting date:', error)
      return ''
    }
  }

  // Parse the form structure from object data only
  useEffect(() => {
    // Clear parsed form data if column data list is empty or not provided
    if (!satsueiMemoColumnDataList || satsueiMemoColumnDataList.length === 0) {
      setParsedFormData([])
      return
    }

    const formElements = []
    let currentSection = null

    // Process each column data
    satsueiMemoColumnDataList.forEach(columnData => {
      // Skip deleted columns
      if (columnData.deleteFlg === 1) {
        return
      }

      // Skip columnType 15 and 16
      if (columnData.columnType === 15 || columnData.columnType === 16) {
        return
      }

      // Handle section titles (type 1)
      if (columnData.columnType === 1) {
        currentSection = {
          type: 'section',
          label: columnData.satsueiMemoColumnName || columnData.key,
          fields: []
        }
        formElements.push(currentSection)
        return
      }

      const field = {
        type: 'field',
        key: columnData.key,
        deleteFlg: columnData.deleteFlg,
        initialValue: columnData.initialValue,
        satsueiMemoCategoryId: columnData.satsueiMemoCategoryId,
        satsueiMemoColumnId: columnData.satsueiMemoColumnId,
        satsueiMemoColumnName: columnData.satsueiMemoColumnName,
        label: columnData.satsueiMemoColumnName || columnData.key,
        value: (formData && formData[columnData.key] !== undefined) ? formData[columnData.key] : (columnData.value || columnData.initialValue || ''),
        columnType: columnData.columnType?.toString(),
        required: columnData.requiredFlg === 1,
        options: []
      }

      // Extract options for choice fields
      if (columnData.satsueiMemoColumnValueList && Array.isArray(columnData.satsueiMemoColumnValueList)) {
        columnData.satsueiMemoColumnValueList.forEach(optionData => {
          // Skip deleted options
          if (optionData.deleteFlg === 1) {
            return
          }

          field.options.push({
            deleteFlg: optionData.deleteFlg,
            initialValueFlg: optionData.initialValueFlg,
            key: optionData.key,
            satsueiMemoCategoryId: optionData.satsueiMemoCategoryId,
            satsueiMemoColumnId: optionData.satsueiMemoColumnId,
            satsueiMemoColumnValueId: optionData.satsueiMemoColumnValueId,
            satsueiMemoColumnValueName: optionData.satsueiMemoColumnValueName,
            value: optionData.value,
            label: optionData.satsueiMemoColumnValueName || optionData.key
          })
        })

          // Determine which options are selected using the same logic as the hook
          // First, check if user has made any selections (value === "1")
          let selectedOptions = columnData.satsueiMemoColumnValueList
            .filter(option => option.deleteFlg === 0 && option.value === "1")
            .map(option => option.key)

          // If no user selections, fall back to initial values (initialValueFlg === 1)
          if (selectedOptions.length === 0) {
            selectedOptions = columnData.satsueiMemoColumnValueList
              .filter(option => option.deleteFlg === 0 && option.initialValueFlg === 1)
              .map(option => option.key)
          }

          // Set the field value based on the field type
          if (selectedOptions.length > 0 && !field.value) {
            if (field.columnType === '6' || field.columnType === '7') {
              // For checkboxes and multi-select, join multiple values with commas
              field.value = selectedOptions.join(',')
            } else {
              // For radio buttons and dropdowns, use the first selected value
              field.value = selectedOptions[0]
            }
          }
      }

      // Fallback: if no options found but this should be a choice field, create default options
      if (field.options.length === 0 && (field.columnType === '5' || field.columnType === '6' || field.columnType === '7' || field.columnType === '8' ||
        field.columnType === '11' || field.columnType === '12' || field.columnType === '13' || field.columnType === '14')) {
        // Create default options based on column type
        const optionCount = field.columnType === '11' ? 2 :
          field.columnType === '12' ? 3 :
            field.columnType === '13' ? 5 :
              field.columnType === '14' ? 10 : 3

        for (let i = 0; i < optionCount; i++) {
          const optionValue = (i + 1).toString()
          field.options.push({
            value: optionValue,
            label: String.fromCharCode(97 + i), // a, b, c, etc.
            checked: field.value === optionValue
          })
        }
      }

      // Add field to current section or as individual field
      if (currentSection) {
        currentSection.fields.push(field)
      } else {
        formElements.push(field)
      }
    })

    setParsedFormData(formElements)
  }, [satsueiMemoColumnDataList, formData])

  // Render form field based on column type
  const renderField = (field) => {
    const handleChange = (value) => {
      if (isReadOnly) return
      
      // Convert HTML date/datetime format back to backend format for storage
      let convertedValue = value

      if (field.columnType === '9') {
        // Convert "2025-09-03" (DateTimePicker date-only format) back to "2025/09/03"
        if (value && value.trim() !== '') {
          // DateTimePicker uses "YYYY-MM-DD" format for date-only, just replace dashes with slashes
          convertedValue = value.replace(/-/g, '/')
        } else {
          convertedValue = ''
        }
      } else if (field.columnType === '10') {
        // Convert "2025-09-02 11:00" (DateTimePicker format) back to "2025/09/02 11:00"
        if (value && value.trim() !== '') {
          // DateTimePicker uses "YYYY-MM-DD HH:mm" format, just replace dashes with slashes
          convertedValue = value.replace(/-/g, '/')
        } else {
          convertedValue = ''
        }
      }

      updateFormData(field.key, convertedValue)
    }

    const commonInputProps = {
      className: `w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
      }`,
      readOnly: isReadOnly,
      disabled: isReadOnly
    }

    switch (field.columnType) {
      case '2': // Single line text
        return (
          <input
            id={`field-${field.key}`}
            data-testid={`text-input-${field.key}`}
            type="text"
            value={(formData && formData[field.key] !== undefined) ? formData[field.key] : field.value}
            onChange={(e) => handleChange(e.target.value)}
            {...commonInputProps}
            required={field.required && !isReadOnly}
            maxLength={3000}
            autoComplete='off'
            aria-label={field.label}
          />
        )

      case '3': // Multi-line text
        return (
          <textarea
            id={`field-${field.key}`}
            data-testid={`textarea-${field.key}`}
            value={(formData && formData[field.key] !== undefined) ? formData[field.key] : field.value}
            onChange={(e) => handleChange(e.target.value)}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical ${
              isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
            rows={3}
            required={field.required && !isReadOnly}
            maxLength={3000}
            readOnly={isReadOnly}
            disabled={isReadOnly}
            aria-label={field.label}
          />
        )

      case '4': // Number
        return (
          <div className="relative">
            <input
              id={`field-${field.key}`}
              data-testid={`number-input-${field.key}`}
              type="number"
              value={(formData && formData[field.key] !== undefined) ? formData[field.key] : field.value}
              onChange={(e) => handleChange(e.target.value)}
              {...commonInputProps}
              required={field.required && !isReadOnly}
              autoComplete='off'
              aria-label={field.label}
            />
          </div>
        )

      case '17': // Decimal number
        return (
          <div className="relative">
            <input
              id={`field-${field.key}`}
              data-testid={`decimal-input-${field.key}`}
              type="number"
              step="0.1"
              value={(formData && formData[field.key] !== undefined) ? formData[field.key] : field.value}
              onChange={(e) => handleChange(e.target.value)}
              {...commonInputProps}
              required={field.required && !isReadOnly}
              autoComplete='off'
              aria-label={field.label}
            />
          </div>
        )

      case '5': { // Radio buttons - single selection only
        const radioValue = (formData && formData[field.key] !== undefined) ? formData[field.key] : field.value
        return (
          <div className="flex gap-2 flex-wrap" data-testid={`radio-group-${field.key}`} role="radiogroup" aria-label={field.label}>
            {field.options.map((option, index) => {
              const isSelected = radioValue === option.key
              if (isReadOnly) {
                return (
                  <div
                    key={index}
                    className={`px-3 py-2 border rounded-md text-sm ${
                      isSelected
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-100 text-gray-500 border-gray-300'
                    }`}
                  >
                    {option.satsueiMemoColumnValueName}
                  </div>
                )
              }
              return (
                <button
                  key={index}
                  type="button"
                  data-testid={`radio-option-${field.key}-${option.key}`}
                  onClick={() => handleChange(option.key)}
                  className={`px-3 py-2 border rounded-md text-sm transition-colors ${isSelected
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={option.satsueiMemoColumnValueName}
                >
                  {option.satsueiMemoColumnValueName}
                </button>
              )
            })}
          </div>
        )
      }

      case '6': { // Checkboxes - multiple selection
        const selectedValues = (((formData && formData[field.key] !== undefined) ? formData[field.key] : field.value) || '').split(',').filter(v => v.trim())
        return (
          <div className="flex gap-2 flex-wrap" data-testid={`checkbox-group-${field.key}`} role="group" aria-label={field.label}>
            {field.options.map((option, index) => {
              if (isReadOnly) {
                return (
                  <div
                    key={index}
                    className={`px-3 py-2 border rounded-md text-sm ${
                      selectedValues.includes(option.key)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-100 text-gray-500 border-gray-300'
                    }`}
                  >
                    {option.satsueiMemoColumnValueName}
                  </div>
                )
              }
              return (
                <button
                  key={index}
                  type="button"
                  data-testid={`checkbox-option-${field.key}-${option.key}`}
                  onClick={() => {
                    const isSelected = selectedValues.includes(option.key)
                    const newValues = isSelected
                      ? selectedValues.filter(v => v !== option.key)
                      : [...selectedValues, option.key]
                    handleChange(newValues.join(','))
                  }}
                  className={`px-3 py-2 border rounded-md text-sm transition-colors ${selectedValues.includes(option.key)
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  role="checkbox"
                  aria-checked={selectedValues.includes(option.key)}
                  aria-label={option.satsueiMemoColumnValueName}
                >
                  {option.satsueiMemoColumnValueName}
                </button>
              )
            })}
          </div>
        )
      }

      case '7': // Multi-select dropdown
        return <MultiSelectDropdown field={field} formData={formData} handleChange={handleChange} isReadOnly={isReadOnly} />

      case '8': // Dropdown
        return (
          <div className="relative">
            <select
              id={`field-${field.key}`}
              data-testid={`dropdown-${field.key}`}
              value={(formData && formData[field.key] !== undefined) ? formData[field.key] : field.value}
              onChange={(e) => handleChange(e.target.value)}
              className={`w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white ${
                isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              required={field.required && !isReadOnly}
              disabled={isReadOnly}
              aria-label={field.label}
            >
              <option value="">選択してください</option>
              {field.options.map((option, index) => (
                <option key={index} value={option.key}>
                  {option.satsueiMemoColumnValueName}
                </option>
              ))}
            </select>
            {/* CSS-only dropdown arrow */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              <div className="w-2 h-2 border-b-2 border-r-2 border-black rotate-45"></div>
            </div>
          </div>
        )

      case '9': // Date
        if (isReadOnly) {
          const displayValue = formData[field.key] !== undefined ? formData[field.key] : field.value
          return (
            <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700">
              {displayValue || '-'}
            </div>
          )
        }
        return (
          <DateTimePicker
            value={convertBackendDateToInputFormat(formData[field.key] !== undefined ? formData[field.key] : field.value, false)}
            onChange={handleChange}
            placeholder="日付を選択"
            dateOnly={true}
            data-testid={`date-input-${field.key}`}
          />
        )

      case '10': // Date and time
        if (isReadOnly) {
          const displayValue = formData[field.key] !== undefined ? formData[field.key] : field.value
          return (
            <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700">
              {displayValue || '-'}
            </div>
          )
        }
        return (
          <DateTimePicker
            value={convertBackendDateToInputFormat(formData[field.key] !== undefined ? formData[field.key] : field.value, true)}
            onChange={handleChange}
            placeholder="日時を選択"
            data-testid={`datetime-input-${field.key}`}
          />
        )

      case '11': // 2-stage selection (radio buttons)
      case '12': // 3-stage selection (radio buttons)
      case '13': // 5-stage selection (radio buttons)
      case '14': // 10-stage selection (radio buttons)
        return (
          <div className="flex gap-2 flex-wrap" data-testid={`n-stage-group-${field.key}`} role="radiogroup" aria-label={field.label}>
            {field.options.map((option, index) => {
              const isSelected = ((formData && formData[field.key] !== undefined) ? formData[field.key] : field.value) === option.key
              if (isReadOnly) {
                return (
                  <div
                    key={index}
                    className={`px-3 py-2 border rounded-md text-sm ${
                      isSelected
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-gray-100 text-gray-500 border-gray-300'
                    }`}
                  >
                    {option.satsueiMemoColumnValueName}
                  </div>
                )
              }
              return (
                <button
                  key={index}
                  type="button"
                  data-testid={`n-stage-option-${field.key}-${option.key}`}
                  onClick={() => handleChange(option.key)}
                  className={`px-3 py-2 border rounded-md text-sm transition-colors ${isSelected
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={option.satsueiMemoColumnValueName}
                >
                  {option.satsueiMemoColumnValueName}
                </button>
              )
            })}
          </div>
        )

      case '15':
      case '16':
        return null

      default:
        return (
          <input
            type="text"
            value={(formData && formData[field.key] !== undefined) ? formData[field.key] : field.value}
            onChange={(e) => handleChange(e.target.value)}
            {...commonInputProps}
            required={field.required && !isReadOnly}
          />
        )
    }
  }

  // Render form section
  const renderSection = (section) => {
    return (
      <div key={section.label} className="mb-6" data-testid={`form-section-${section.label}`}>
        <div className="bg-gray-800 text-white font-semibold text-sm px-3 py-2 rounded-md mb-3 tracking-wide">
          {section.label}
        </div>
        <div className="space-y-4">
          {section.fields.map((field, index) => (
            <div key={index} className="flex items-start gap-4" data-testid={`field-wrapper-${field.key}`}>
              <label htmlFor={`field-${field.key}`} className="flex-shrink-0 min-w-0 w-32 text-sm font-medium text-gray-700 pt-2 break-words">
                {field.label || field.key}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <div className="flex-1 min-w-0">
                {renderField(field)}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Render individual field
  const renderIndividualField = (field) => {
    return (
      <div key={field.key} className="flex items-start gap-4 mb-4" data-testid={`field-wrapper-${field.key}`}>
        <label htmlFor={`field-${field.key}`} className="flex-shrink-0 min-w-0 w-32 text-sm font-medium text-gray-700 pt-2 break-words">
          {field.label || field.key}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="flex-1 min-w-0">
          {renderField(field)}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-testid="satsuei-data-form-area">
      {/* Top Section - Registration Info and Category Selection Side by Side - Hidden in share and view modes */}
      {mode === 'edit' && (
        <div className="p-4 border-b border-gray-200" data-testid="form-header-section">
        <div className="grid grid-cols-2 gap-6">
          {/* Left Side - Category Selection */}
          <div className="space-y-3">
            <div>
              <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-1">
                撮影カテゴリー
              </label>
              <div className="relative">
                <select
                  id="category-select"
                  data-testid="category-select"
                  value={satsueiMemoCategoryId}
                  onChange={(e) => onCategoryChange(e.target.value)}
                  className={`w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white ${
                    isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  disabled={isReadOnly}
                  aria-label="撮影カテゴリーを選択"
                >
                  {satsueiMemoCategories.map((category) => (
                    <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                      {category.satsueiMemoCategoryName}
                    </option>
                  ))}
                </select>
                {/* CSS-only dropdown arrow */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <div className="w-2 h-2 border-b-2 border-r-2 border-black rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Kyoten selection - only show in edit mode */}
            {mode === 'edit' && (
              <div>
                <label htmlFor="kyoten-select" className="block text-sm font-medium text-gray-700 mb-1">
                  拠点
                </label>
                <div className="relative">
                  <select
                    id="kyoten-select"
                    data-testid="kyoten-select"
                    value={kyotenId}
                    onChange={(e) => setKyotenId(e.target.value)}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white"
                    aria-label="拠点を選択"
                  >
                    <option value="">未指定</option>
                    {kyotenList.map((kyoten) => (
                      <option key={kyoten.kyotenId} value={kyoten.kyotenId}>
                        {kyoten.kyotenName}
                      </option>
                    ))}
                  </select>
                  {/* CSS-only dropdown arrow */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                    <div className="w-2 h-2 border-b-2 border-r-2 border-black rotate-45"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Side - Registration Info */}
          {hasData && (
            <div className="space-y-2 text-sm" data-testid="registration-info">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">初期登録者:</span>
                <span className="text-gray-900">{createUserName || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">初期登録時刻:</span>
                <span className="text-gray-900">{createDatetime || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">最終更新者:</span>
                <span className="text-gray-900">{updateUserName || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">最終更新時刻:</span>
                <span className="text-gray-900">{updateDatetime || '-'}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Satsuei Guide Area - only show in edit mode */}
      {mode === 'edit' && satsueiGuideList && satsueiGuideList.length > 0 && (
        <div className="p-4 border-b border-gray-200" data-testid="satsuei-guide-section">
          <div className="flex items-center space-x-2">
            <label htmlFor="satsuei-guide-select" className="text-sm font-medium text-gray-700">撮影ガイド:</label>
            <select
              id="satsuei-guide-select"
              data-testid="satsuei-guide-select"
              value={satsueiGuideId || ''}
              onChange={(e) => setSatsueiGuideId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="撮影ガイドを選択"
            >
              <option value="">使用しない</option>
              {satsueiGuideList.map((guide) => (
                <option key={guide.satsueiGuideId} value={guide.satsueiGuideId}>
                  {guide.satsueiGuideName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Dynamic Form Fields */}
      <div className="p-4" data-testid="dynamic-form-fields">
        <div className="space-y-4">
          <div className="border-b border-gray-200 pb-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-900" data-testid="form-title">撮影メモ詳細</h3>
            <p className="text-sm text-gray-600">
              {isReadOnly ? 'カテゴリーに応じた詳細情報' : 'カテゴリーに応じた詳細情報を入力してください'}
            </p>
          </div>

          {parsedFormData.length > 0 ? (
            <div ref={formRef} data-testid="form-fields-container">
              {parsedFormData.map((item) => (
                item.type === 'section' ? renderSection(item) : renderIndividualField(item)
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8" data-testid="empty-form-message">
              {isReadOnly ? 'フォームデータがありません' : 'カテゴリーを選択してください'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SatsueiDataFormArea
